export type Subscriber<State> = (s: State) => void;

export type UnsubscribeCallback = () => void;

export class StoreHasNoDataError extends Error {
  constructor() {
    super('Данные отсутствуют. Попробуйте повторно инициализировать стор');
  }
}

export class StoreIsNotFailedError extends Error {
  constructor() {
    super('Нельзя получить состояние ошибки инициализации, потому что store не в состоянии ошибки инициализации');
  }
}

export enum StoreStateStatus {
  Idle = 'Idle',
  Processing = 'Processing',
  Success = 'Success',
  Error = 'Error',
}

export type StoreState<StoreData, StoreError> =
  | StoreStateError<StoreData, StoreError>
  | StoreStateProcessing<StoreData>
  | StoreStateIdle<StoreData>
  | StoreStateSuccess<StoreData>;

export interface StoreStateSuccess<StoreData> {
  status: StoreStateStatus.Success;
  data: StoreData;
  error: undefined;
}

export interface StoreStateError<StoreData, StoreError> {
  status: StoreStateStatus.Error;
  data: StoreData | undefined;
  error: StoreError;
}

export interface StoreStateProcessing<StoreData> {
  status: StoreStateStatus.Processing;
  data: StoreData | undefined;
  error: undefined;
}

export interface StoreStateIdle<StoreData> {
  status: StoreStateStatus.Idle;
  data: StoreData | undefined;
  error: undefined;
}

export class StatefulStore<StoreData, StoreError = unknown> {
  private readonly listeners = new Set<Subscriber<StoreState<StoreData, StoreError>>>();

  protected state: StoreState<StoreData, StoreError>;

  constructor() {
    this.state = {
      status: StoreStateStatus.Idle,
      data: undefined,
      error: undefined,
    };
  }

  public resetState = (): void => {
    this.setState({
      status: StoreStateStatus.Idle,
      data: undefined,
      error: undefined,
    });
  };

  public getState = (): StoreState<StoreData, StoreError> => {
    return this.state;
  };

  public getDataOrFail = (): StoreData => {
    // Специально смотрим только на this.state.data, потому что
    // при повторной инициализации не хотим обнулять сохраненные данные
    if (typeof this.state.data === 'undefined') {
      throw new StoreHasNoDataError();
    }

    return this.state.data;
  };

  public getStoreError = (): StoreError => {
    if (this.state.status !== StoreStateStatus.Error) {
      throw new StoreIsNotFailedError();
    }

    return this.state.error;
  };

  public isIdle = (): boolean => {
    return this.state.status === StoreStateStatus.Idle;
  };

  public isProcessing = (): boolean => {
    return this.state.status === StoreStateStatus.Processing;
  };

  public isSuccess = (): boolean => {
    return this.state.status === StoreStateStatus.Success;
  };

  public isError = (): boolean => {
    return this.state.status === StoreStateStatus.Error;
  };

  public toProcessing = (): StoreState<StoreData, StoreError> => {
    return this.setState({
      status: StoreStateStatus.Processing,
      data: this.state.data,
      error: undefined,
    });
  };

  public restoreState = (): StoreState<StoreData, StoreError> => {
    return this.setState({
      status: StoreStateStatus.Idle,
      data: this.state.data,
      error: undefined,
    });
  };

  public toSuccess = (data: StoreData): StoreState<StoreData, StoreError> => {
    return this.setState({
      status: StoreStateStatus.Success,
      data,
      error: undefined,
    });
  };

  public toError = (error: StoreError): StoreState<StoreData, StoreError> => {
    return this.setState({
      data: this.state.data,
      status: StoreStateStatus.Error,
      error,
    });
  };

  public setState = (state: StoreState<StoreData, StoreError>): StoreState<StoreData, StoreError> => {
    this.state = state;

    this.listeners.forEach((listener) => {
      listener(this.state);
    });

    return this.state;
  };

  public subscribe = (listener: Subscriber<StoreState<StoreData, StoreError>>): UnsubscribeCallback => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };
}
